import { createPortal } from "react-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Receipt, X } from "lucide-react";
import { formatDate } from "@/utils/dateUtils";

export default function PrescriptionInvoicesModal({
  isOpen,
  onClose,
  invoices = [],
  isLoading = false,
  patientName,
}) {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Receipt className="w-6 h-6 text-green-600" />
              Invoices for Prescription
            </h2>
            {patientName && (
              <p className="text-sm text-slate-600 mt-1">
                Patient: {patientName}
              </p>
            )}
          </div>
          <Button variant="ghost" onClick={onClose} className="gap-2">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-slate-600">Loading invoices...</p>
            </div>
          ) : invoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Receipt className="w-16 h-16 text-slate-300 mb-4" />
              <p className="text-slate-600 text-lg">No invoices found</p>
              <p className="text-slate-500 text-sm mt-2">
                Generate an invoice for this prescription to see it here
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  {/* Invoice Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">
                        Invoice #
                        {invoice.xeroInvoiceNumber || invoice.id.substring(0, 8)}
                      </h3>
                      <p className="text-sm text-slate-600">
                        {formatDate(invoice.invoiceDate, "MMMM d, yyyy")}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">
                        R{invoice.total.toFixed(2)}
                      </p>
                      <Badge
                        className={`mt-1 ${
                          invoice.status === "PAID"
                            ? "bg-green-100 text-green-700 border-green-200"
                            : invoice.status === "DRAFT"
                              ? "bg-gray-100 text-gray-700 border-gray-200"
                              : "bg-yellow-100 text-yellow-700 border-yellow-200"
                        }`}
                      >
                        {invoice.status}
                      </Badge>
                    </div>
                  </div>

                  {/* Invoice Line Items */}
                  {invoice.lineItems && invoice.lineItems.length > 0 && (
                    <div className="mt-4 border-t pt-3">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">
                        Items:
                      </h4>
                      <div className="space-y-2">
                        {invoice.lineItems.map((item, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between text-sm"
                          >
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">
                                {item.description}
                              </p>
                              <p className="text-slate-600">
                                {item.quantity} x R{item.unitPrice.toFixed(2)}
                              </p>
                            </div>
                            <p className="font-semibold text-slate-900">
                              R{item.lineAmount.toFixed(2)}
                            </p>
                          </div>
                        ))}
                      </div>

                      {/* Totals Breakdown */}
                      <div className="mt-3 pt-3 border-t space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Subtotal:</span>
                          <span className="font-medium text-slate-900">
                            R{(invoice.subtotal ?? invoice.total - (invoice.taxAmount ?? 0)).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">
                            VAT ({((invoice.taxRate ?? 0.15) * 100).toFixed(0)}%):
                          </span>
                          <span className="font-medium text-slate-900">
                            R{(invoice.taxAmount ?? 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm font-semibold pt-1 border-t">
                          <span className="text-slate-900">Total:</span>
                          <span className="text-green-600">
                            R{invoice.total.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Xero Sync Status */}
                  {invoice.xeroInvoiceId && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                          Synced to Xero
                        </Badge>
                        {invoice.syncedAt && (
                          <span>
                            {formatDate(invoice.syncedAt, "MMM d, yyyy")}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="border-t p-4 bg-slate-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">
              {invoices.length} invoice{invoices.length !== 1 ? "s" : ""} found
            </p>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
